import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { asArray } from "@/lib/arrays";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  HeadphonesIcon,
  Plus,
  MessageSquare,
  ExternalLink,
  Clock,
  CheckCircle,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  HelpCircle
} from "lucide-react";
import { toast } from "sonner";

const SupportPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showFaqModal, setShowFaqModal] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [newTicket, setNewTicket] = useState({
    subject: "",
    description: "",
    type: "general",
    priority: "medium"
  });

  const fetchTickets = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/support/tickets`, { withCredentials: true });
      setTickets(asArray(response.data, "tickets"));
    } catch (error) {
      console.error("Error fetching tickets:", error); // eslint-disable-line no-console
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const createTicket = async () => {
    if (!newTicket.subject || !newTicket.description) {
      toast.error("Please fill in all fields");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/support/tickets`, newTicket, { withCredentials: true });
      toast.success("Support ticket created");
      setNewTicket({ subject: "", description: "", type: "general", priority: "medium" });
      setDialogOpen(false);
      fetchTickets();
    } catch (error) {
      toast.error("Failed to create ticket");
    } finally {
      setCreating(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "open":
        return <AlertCircle className="w-4 h-4" />;
      case "in_progress":
        return <Clock className="w-4 h-4" />;
      case "resolved":
      case "closed":
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <MessageSquare className="w-4 h-4" />;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "open":
        return "bg-[#F59E0B]/20 text-[#F59E0B]";
      case "in_progress":
        return "bg-[#2563EB]/20 text-[#2563EB]";
      case "resolved":
        return "bg-[#10B981]/20 text-[#10B981]";
      case "closed":
        return "bg-[#64748B]/20 text-[#64748B]";
      default:
        return "bg-[#64748B]/20 text-[#64748B]";
    }
  };

  const getPriorityClass = (priority) => {
    switch (priority) {
      case "high":
        return "bg-[#EF4444]/20 text-[#EF4444]";
      case "medium":
        return "bg-[#F59E0B]/20 text-[#F59E0B]";
      case "low":
        return "bg-[#10B981]/20 text-[#10B981]";
      default:
        return "bg-[#64748B]/20 text-[#64748B]";
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Support">
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#00d991] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Support">
      <div className="space-y-6" data-testid="support-page">
        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-[#1E293B] cursor-pointer card-hover" data-testid="create-ticket-card">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#00d991]/20 flex items-center justify-center">
                    <Plus className="w-6 h-6 text-[#00d991]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">Create Ticket</p>
                    <p className="text-sm text-gray-500 dark:text-[#64748B]">Raise a new support request</p>
                  </div>
                </CardContent>
              </Card>
            </DialogTrigger>
            <DialogContent className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-white">
              <DialogHeader>
                <DialogTitle className="font-outfit">Create Support Ticket</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-gray-500 dark:text-[#94A3B8]">Subject</Label>
                  <Input
                    value={newTicket.subject}
                    onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                    placeholder="Brief description of your issue"
                    className="bg-gray-50 dark:bg-[#0A0E17] border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-white"
                    data-testid="ticket-subject-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500 dark:text-[#94A3B8]">Description</Label>
                  <Textarea
                    value={newTicket.description}
                    onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                    placeholder="Describe your issue in detail"
                    className="bg-gray-50 dark:bg-[#0A0E17] border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-white min-h-[100px]"
                    data-testid="ticket-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-500 dark:text-[#94A3B8]">Type</Label>
                    <Select
                      value={newTicket.type}
                      onValueChange={(value) => setNewTicket({ ...newTicket, type: value })}
                    >
                      <SelectTrigger className="bg-gray-50 dark:bg-[#0A0E17] border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-white" data-testid="ticket-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-[#1E293B]">
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="payment">Payment Issue</SelectItem>
                        <SelectItem value="access">Access Issue</SelectItem>
                        <SelectItem value="technical">Technical Problem</SelectItem>
                        <SelectItem value="mentorship">Mentorship Request</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500 dark:text-[#94A3B8]">Priority</Label>
                    <Select
                      value={newTicket.priority}
                      onValueChange={(value) => setNewTicket({ ...newTicket, priority: value })}
                    >
                      <SelectTrigger className="bg-gray-50 dark:bg-[#0A0E17] border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-white" data-testid="ticket-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-[#1E293B]">
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button
                  onClick={createTicket}
                  disabled={creating}
                  className="w-full bg-[#00d991] text-black hover:bg-[#00ff9f]"
                  data-testid="submit-ticket-btn"
                >
                  {creating ? "Creating..." : "Submit Ticket"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Card
            className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-[#1E293B] cursor-pointer card-hover"
            onClick={() => window.open("https://wa.me/919257059363", "_blank")}
            data-testid="whatsapp-support-card"
          >
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-[#10B981]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">WhatsApp Support</p>
                <p className="text-sm text-gray-500 dark:text-[#64748B]">Chat with us directly</p>
              </div>
              <ExternalLink className="w-4 h-4 text-gray-500 dark:text-[#64748B] ml-auto" />
            </CardContent>
          </Card>

          <Card
            onClick={() => setShowFaqModal(true)}
            className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-[#1E293B] cursor-pointer hover:border-blue-500/50 hover:shadow-md transition-all group"
            data-testid="faq-card"
          >
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#2563EB]/20 flex items-center justify-center group-hover:scale-105 transition-transform">
                <HeadphonesIcon className="w-6 h-6 text-[#2563EB]" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900 dark:text-white group-hover:text-blue-500 transition-colors">FAQ</p>
                <p className="text-sm text-gray-500 dark:text-[#64748B]">Common questions answered</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition-transform" />
            </CardContent>
          </Card>
        </div>

        {/* Interactive FAQ Dialog Modal (M2) */}
        <Dialog open={showFaqModal} onOpenChange={setShowFaqModal}>
          <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto bg-white dark:bg-[#0E131F] border-gray-200 dark:border-white/10 text-gray-900 dark:text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg font-outfit font-bold">
                <HelpCircle className="w-5 h-5 text-blue-500" /> Frequently Asked Questions
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-3 mt-3">
              {[
                {
                  q: "How does Drishti AI institutional trade detection work?",
                  a: "Drishti AI scans real-time price action across NSE equities and indices using proprietary institutional demand/supply zone algorithms. Each alert provides quantified score, Entry price, Stop-Loss, and Target milestones with strict risk-to-reward ratios."
                },
                {
                  q: "How do I connect my Dhan demat account?",
                  a: "Navigate to your Profile or Drishti AI page and click 'Connect Dhan'. Enter your Dhan Client ID and 30-day Access Token. Once linked, you can execute alerts with 1-click order execution."
                },
                {
                  q: "How do I unlock Drishti AI Pro access?",
                  a: "Drishti AI Pro is included free for students enrolled in our Pro courses (Super Trader Edge, Super Option Analysis, or Super Trader Combo). You can also submit an access request from the Drishti Pro banner to be reviewed and activated by admin."
                },
                {
                  q: "How do Jamadhan referral rewards work?",
                  a: "Every student has a unique referral code. When a friend signs up and completes a purchase using your code, Jamadhan coins are automatically deposited into your wallet balance for ecosystem rewards."
                },
                {
                  q: "Where do I access Zoom live class recordings and study notes?",
                  a: "All recorded classes are linked directly inside the course curriculum within 24 hours of the live session. You can also take personal rich-text notes with multi-paragraph support on any lesson."
                },
                {
                  q: "How quickly does the support team respond?",
                  a: "Support tickets are monitored during trading hours (9:00 AM – 6:00 PM IST). Average first response time is under 2 hours. For critical emergencies, reach out on the official WhatsApp support channel."
                }
              ].map((faq, idx) => {
                const isOpen = openFaqIndex === idx;
                return (
                  <div
                    key={idx}
                    className="border border-gray-200 dark:border-white/[0.08] rounded-xl overflow-hidden transition-all bg-gray-50/50 dark:bg-white/[0.02]"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaqIndex(isOpen ? -1 : idx)}
                      className="w-full text-left p-4 flex items-center justify-between gap-3 hover:bg-gray-100/50 dark:hover:bg-white/[0.04] transition-colors"
                    >
                      <span className="font-semibold text-sm text-gray-900 dark:text-white font-outfit">{faq.q}</span>
                      <ChevronDown className={`w-4 h-4 shrink-0 text-gray-400 transition-transform ${isOpen ? "rotate-180 text-blue-500" : ""}`} />
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-4 text-xs text-gray-600 dark:text-[#94A3B8] leading-relaxed border-t border-gray-100 dark:border-white/[0.04] pt-3 font-plex">
                        {faq.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>

        {/* Tickets List */}
        <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-[#1E293B]" data-testid="tickets-list">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-white font-outfit">Your Tickets</CardTitle>
          </CardHeader>
          <CardContent>
            {tickets.length === 0 ? (
              <div className="text-center py-8">
                <HeadphonesIcon className="w-12 h-12 text-gray-500 dark:text-[#64748B] mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-gray-500 dark:text-[#94A3B8]">No support tickets yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {tickets.map((ticket) => (
                  <div
                    key={ticket.ticket_id}
                    className="bg-gray-50 dark:bg-[#0A0E17] border border-gray-200 dark:border-[#1E293B] rounded-lg p-4"
                    data-testid={`ticket-${ticket.ticket_id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Badge className={getStatusClass(ticket.status)}>
                            {getStatusIcon(ticket.status)}
                            <span className="ml-1 capitalize">{ticket.status.replace("_", " ")}</span>
                          </Badge>
                          <Badge className={getPriorityClass(ticket.priority)}>
                            {ticket.priority}
                          </Badge>
                          <Badge variant="outline" className="text-gray-500 dark:text-[#94A3B8] border-gray-200 dark:border-[#1E293B]">
                            {ticket.type}
                          </Badge>
                        </div>
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{ticket.subject}</h3>
                        <p className="text-sm text-gray-500 dark:text-[#64748B] line-clamp-2">{ticket.description}</p>
                        <p className="text-xs text-gray-500 dark:text-[#64748B] mt-2">
                          Created: {new Date(ticket.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    {ticket.responses && ticket.responses.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-[#1E293B]">
                        <p className="text-xs text-gray-500 dark:text-[#64748B] mb-2">Responses:</p>
                        {ticket.responses.map((response, idx) => (
                          <div key={idx} className="bg-white dark:bg-[#131B2C] rounded p-3 mb-2">
                            <p className="text-sm text-gray-900 dark:text-white">{response.message}</p>
                            <p className="text-xs text-gray-500 dark:text-[#64748B] mt-1">
                              {response.responder_name} • {new Date(response.created_at).toLocaleString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default SupportPage;
